import Box from '@mui/material/Box';
import { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useInlineEditContext } from '@/components/EditableSectionField/InlineEditContext';
import { InlineEditTriggerButton } from '@/components/EditableSectionField/InlineEditTriggerButton/InlineEditTriggerButton';
import type { InlineFieldKey } from '@/core/dcr/fields/registry';
import { useTouchEditVisible } from '@/core/store/touchEdit/touchEditSelectors';
import { mergeSx } from '@/utils/mergeSx';

import { useSectionFieldStyles as useStyles } from './SectionField.styles';
import { SectionFieldContent } from './SectionFieldContent';

export type SectionValue = {
	name: string;
	isPrimary?: boolean;
	isSecondary?: boolean;
	link?: string | null;
};

export type Value =
	| string
	| string[]
	| SectionValue
	| SectionValue[]
	| Record<string, SectionValue[]>
	| boolean
	| null
	| undefined;

type SectionFieldPropsBase = {
	label: ReactNode;
	to?: string | null;
	link?: string | null;
	tooltip?: string | null;
	collapseAt?: Breakpoint;
	labelWidth?: CSSProperties['width'];
	hideLabel?: boolean;
	editFieldName?: InlineFieldKey;
	customSx?: {
		root?: SxProps<Theme>;
		label?: SxProps<Theme>;
		groupName?: SxProps<Theme>;
		value?: SxProps<Theme>;
	};
};

type WithValue = {
	value: Value;
	children?: never;
};

type WithChildren = {
	value?: never;
	children: ReactNode;
};

export type SectionFieldProps = SectionFieldPropsBase &
	(WithValue | WithChildren);

const isValueSet = (value: Value) => {
	if (value === undefined || value === null) {
		return false;
	}

	if (typeof value === 'string') {
		return value.trim() !== '';
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	if (typeof value === 'object' && value !== null) {
		return Object.keys(value).length > 0;
	}

	return true;
};

export const SectionField = ({
	label,
	value,
	to,
	link,
	tooltip,
	collapseAt,
	labelWidth,
	hideLabel = false,
	children,
	editFieldName,
	customSx
}: SectionFieldProps) => {
	const { t } = useTranslation();
	const sx = useStyles(collapseAt, labelWidth);
	const touchEditVisible = useTouchEditVisible();
	const ctx = useInlineEditContext();
	const editableField = editFieldName
		? ctx?.getEditableField(editFieldName)
		: undefined;
	const canEdit =
		!!ctx?.enabled && !!editableField?.editor && editableField.isAvailable;
	const isLocked = !!ctx?.enabled && !ctx.unavailableFieldsLoading && !canEdit;

	if (!isValueSet(value) && !children) return null;

	const content = children ? (
		children
	) : (
		<SectionFieldContent
			value={value}
			to={to}
			link={link}
			tooltip={tooltip}
			customSx={customSx}
		/>
	);

	return (
		<Box
			sx={mergeSx(
				sx.fieldContainer,
				!!editFieldName && sx.editableField,
				!!editFieldName && sx.editableFieldTouch(touchEditVisible),
				isLocked && sx.lockedField,
				customSx?.root
			)}
		>
			{!hideLabel && (
				<Typography
					variant='body2'
					component='div'
					sx={mergeSx(sx.label, customSx?.label)}
				>
					{label}
				</Typography>
			)}

			<Box sx={sx.valueContainer}>
				{canEdit ? (
					<Box sx={sx.editableValue}>
						<Box sx={sx.editableValueList}>{content}</Box>
						<InlineEditTriggerButton
							onClick={() => ctx.requestActivate(editableField.fieldName)}
							ariaLabel={t('Edit {{label}}', { label: editableField.label })}
							markerAttr='data-pen'
						/>
					</Box>
				) : isLocked ? (
					<Box sx={sx.editableValue}>
						<Box sx={sx.editableValueList}>{content}</Box>
						<Box component='span' sx={sx.notEditableHint} data-locked>
							{t('Not editable')}
						</Box>
					</Box>
				) : (
					content
				)}
			</Box>
		</Box>
	);
};
